import { useQuery } from "@tanstack/react-query";

import { getPrintImages } from "@/api/print-image-api";
import { type PrintImage } from "@/schemas";
import { FIVE_MINUTES_MS } from "@/constants";

type DropdownOption = {
	key: number;
	text: string;
	value: string;
};

type PrintImagesResult = {
	printImages: PrintImage[];
	pdfOptions: DropdownOption[];
};

const filterAndFormatPrintImages = (
	allImages: PrintImage[],
	length: number,
	width: number,
	height: number,
): PrintImagesResult => {
	const filtered = allImages.filter(
		(image) =>
			image.dimensions &&
			image.dimensions.length === length &&
			image.dimensions.width === width &&
			image.dimensions.height === height,
	);

	const pdfOptions = filtered.map((image, i) => ({
		key: i,
		value: image.id ?? "",
		text: image.alias ?? "",
	}));

	return { printImages: filtered, pdfOptions };
};

export const usePrintImages = (length: number | null, width: number | null, height: number | null) => {
	const hasValidDimensions =
		length !== null && width !== null && height !== null && length > 0 && width > 0 && height > 0;

	const query = useQuery({
		queryKey: ["print-images", length, width, height],
		queryFn: async () => {
			const allImages = await getPrintImages();
			return filterAndFormatPrintImages(allImages, length!, width!, height!);
		},
		enabled: hasValidDimensions,
		staleTime: FIVE_MINUTES_MS,
		retry: false,
	});

	return {
		printImages: query.data?.printImages ?? [],
		pdfOptions: query.data?.pdfOptions ?? [],
		isLoading: query.isLoading,
		error: query.error,
	};
};
